<!--*
 * Screen with the survey questions
 *
 * @author   mauricio.figueroa
 * @date     2020-07-08
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, technique
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div class="container--fluid px-0">
    <SubMenuHeader
      :title="placeName"
      :custom-go-back="true"
      @onGoBack="onSaveQuestions(true)"
      @showVideo="$emit('showVideo')"
    />
    <SurveyStatus
      :total="totalQuestions"
      :progress="progress"
      :is-pausable="pausable"
      :current-saving="saveCallStack.length"
      :hide-on-scroll="false"
    />
    <floating-label
      v-if="saveCallStack.length > 0"
      :text="$t('surveyQuestionScreen.saveCallStack.notification')"
    />
    <div class="row survey-bg m-0">
      <div class="col--lg--6 col--md--8 col--12 mx-auto flex-1">
        <div class="card p-5 mt--md-5 mt-0">
          <smtt-contextual-box
            ref="userErrorBox"
            is-visible="false"
            :message-title="errorMessage"
            variation="error"
            dismissable="true"
          />
          <div v-if="sections.length > 0">
            <div v-if="paginateBySections">
              <Pagination :items="sections" v-slot="{ pageData }">
                <SurveyQuestionsScreenSectionCard
                  :section="pageData[0]"
                  @onShowHideConditional="showHideConditional"
                  :is-view-mode="isViewMode"
                  @onAnswered="answerHandler"
                />
              </Pagination>
            </div>
            <div v-else>
              <div class="pb-5" v-for="section in sections" :key="section.id">
                <SurveyQuestionsScreenSectionCard
                  :section="section"
                  @onShowHideConditional="showHideConditional"
                  @onAnswered="answerHandler"
                  :is-view-mode="isViewMode"
                />
              </div>
            </div>
          </div>
          <div v-else>
            <Question
              v-for="question in questions"
              :key="question.questionId"
              :question="question"
              :view-mode="isViewMode"
              @onAnswered="answerHandler"
              @onShowHideConditional="showHideConditional"
            />
          </div>
        </div>
        <div v-if="!isViewMode" class="card p-5 mt-0">
          <div class="row">
            <div class="col--12 col--md--6 mb--md-4 mb-4">
              <app-button
                full-width
                modification="grey"
                enter-to-tab
                :disabled="isViewMode"
                @click="onSaveQuestions(false)"
              >
                {{ $t("surveyQuestionScreen.button.save") }}
              </app-button>
            </div>
            <div class="col--12 col--md--6 mb-4">
              <app-button
                full-width
                enter-to-tab
                :disabled="mandatoryCompleted || isViewMode"
                @click="onFinishQuestions()"
              >
                {{ this.finishButtonText }}
              </app-button>
            </div>
            <div v-if="showResetFormButton" class="col--12 col--md--6">
              <app-button
                enter-to-tab
                full-width
                modification="grey"
                :disabled="isViewMode"
                @click="isConfirmResetModalVisible = true"
              >
                {{ $t("surveyQuestionScreen.button.resetForm") }}
              </app-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <smtt-toast id="errorToast" ref="statusErrorToast" style="border: 0" />
    <TransitionListModal
      v-if="isTransitionModalVisible"
      :site="siteId"
      :assignment-id="assignmentId"
      :room-id="roomId"
      :disabled="!isRoomCompleted"
      :transitions="transitions"
      :transition-comment="transitionComment"
      :action-enabled="isActionEnabled"
      @onGoBackCancel="cancelTransitionHandler"
      @showError="showError"
      @onTransitioned="updateTransitionsHandler"
    />
    <TransitionCommentModal
      v-if="showCommentModal"
      @onDismiss="modalDismissHandler"
      @onConfirm="modalConfirmHandler"
    />
    <confirm-modal
      v-if="isConfirmResetModalVisible"
      @onConfirm="onResetForm"
      @onCancel="isConfirmResetModalVisible = false"
    >
      <template #title>
        {{ $t("confirmResultModal.title") }}
      </template>
      <template #body>
        {{ $t("confirmResultModal.body") }}
      </template>
    </confirm-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";

import SubMenuHeader from "../SubMenuHeader";
import AppButton from "../AppButton.vue";
import store from "../../store/store";

import SurveyStatus from "./SurveyStatus/SurveyStatus";
import Question from "./Questions/Question";

import TransitionListModal from "./TransitionListModal.vue";
import TransitionCommentModal from "../siteDetails/TransitionCommentModal.vue";
import ConfirmModal from "./ConfirmModal.vue";
import FloatingLabel from "../FloatingLabel.vue";
import Vue from "vue";
import generateString from "../../utils/randomStringGenerator";
import getEnv from "../../utils/env";
import SurveyQuestionsScreenSectionCard from "./SurveyQuestionsScreenSectionCard.vue";
import Pagination from "../utils/Pagination.vue";

export default {
  name: "SurveyQuestionsScreen",
  components: {
    Pagination,
    TransitionListModal,
    Question,
    SurveyStatus,
    SubMenuHeader,
    ConfirmModal,
    TransitionCommentModal,
    AppButton,
    FloatingLabel,
    SurveyQuestionsScreenSectionCard,
  },
  data: function () {
    return {
      errorMessage: null,
      questions: [],
      sections: [],
      totalQuestions: 0,
      progress: 0,
      placeName: "",
      mandatoryCompleted: true,
      // Information needed to save the survey
      roomId: 0,
      isRecurrent: false,
      siteId: 0,
      assignmentId: 0,
      // View mode data
      isViewMode: false,
      // workflow
      transitions: [],
      transitionComment: null,
      phaseName: null,
      isActionEnabled: true,
      isTransitionModalVisible: false,
      saveMode: false,
      isConfirmResetModalVisible: false,
      showCommentModal: false,
      finishButtonText: "",
      saveCallStack: [],
      alreadySavedImages: [],
      pausable: false,
      roomAssignmentId: 0,
      origin: "",
      paginateBySections: false,
    };
  },
  watch: {
    currentLocale() {
      this.finishButtonText = this.getFinishButtonText();
    },
  },
  computed: {
    currentLocale() {
      return store.state.currentLocale;
    },
    isRoomCompleted() {
      if (this.transitions !== null) {
        return !(
          (this.mandatoryCompleted || this.isViewMode) &&
          this.transitions.length > 0
        );
      } else {
        return !(this.mandatoryCompleted || this.isViewMode);
      }
    },
    hasWorkflow() {
      return !this.isRecurrent && !!this.transitions;
    },
    showResetFormButton() {
      const resetSurveyHiddenRoles =
        store.state.resetSurveyButtonHiddenForRoles;
      const userRoles = store.getters.GetUserRoles();

      let showResetSurveyButton = true;
      if (resetSurveyHiddenRoles?.length >= 1) {
        resetSurveyHiddenRoles.forEach((rolToHiddenButton) => {
          userRoles.forEach((rol) => {
            let userRol = rol.role;

            if (rolToHiddenButton.toUpperCase() === userRol.toUpperCase()) {
              showResetSurveyButton = false;
            }
          });
        });
      }

      return showResetSurveyButton;
    },
  },
  methods: {
    ...mapActions([
      "getQuestions",
      "saveRoomAnswers",
      "getTransitionOptions",
      "requestMyLocation",
      "transitionPhase",
      "resetRoomSurvey",
    ]),
    getFinishButtonText() {
      if (this.isRecurrent || !this.hasWorkflow) {
        return this.$t("surveyQuestionScreen.button.submit");
      } else if (this.isRequestForApprovalPhase()) {
        return this.$t("surveyQuestionScreen.button.sendForApproval");
      } else {
        return this.$t("surveyQuestionScreen.button.workflowSubmit");
      }
    },
    isRequestForApprovalPhase() {
      let response = false;
      if (this.transitions !== null && this.transitions.length > 0) {
        let filteredTransitions = this.transitions.filter(
          (transition) => transition.name === "Request for approval"
        );

        if (filteredTransitions.length > 0) {
          response = true;
        }
      }

      return response;
    },
    cancelTransitionHandler() {
      this.isTransitionModalVisible = false;
    },
    showHideConditional(option, show) {
      const conditionalQuestionIds = option.conditional
        .split(";")
        .map((id) => parseInt(id));
      const questions = this.questions.filter((q) =>
        conditionalQuestionIds.includes(q.questionId)
      );

      for (const question of questions) {
        if (!show) {
          question.answerStatus = 1;
          question.questionOptions.forEach((o) => {
            if (o.selected) {
              o.selected = false;
              if (o.conditional) {
                this.showHideConditional(o, false);
              }
            }
          });
        }
        question.isVisible = show;
      }

      this.countTotalQuestionsForProgressBar();
    },
    checkMandatorys() {
      for (const question of this.questions) {
        if (question.mandatory === 1 && question.answerStatus !== 2) {
          this.mandatoryCompleted = true;
          break;
        } else {
          this.mandatoryCompleted = false;
        }
      }
    },
    answerHandler: function (response) {
      // replace the answered question in the state data
      this.questions = this.questions.map((question) => {
        if (question.questionId === response.questionId) {
          this.updateQuestionInSections(response);
          return response;
        } else {
          return question;
        }
      });
      // If using sections, update the section questions too
      if (this.sections?.length > 0) {
        this.sections = this.sections.map((section) => {
          section.sectionQuestions = section.sectionQuestions.map(
            (question) => {
              if (question.questionId === response.questionId) {
                return response;
              }
              return question;
            }
          );

          return section;
        });
      }

      // update progress status
      this.checkMandatorys();
      this.updateProgressStatus();

      // Auto-Save answers if it's enabled by environment configs
      if (getEnv("VUE_APP_ENABLE_AUTOSAVE")?.toLowerCase() === "true") {
        this.saveCallHandler(response);
      }
    },
    updateQuestionInSections(newQuestion) {
      this.sections.forEach((section) => {
        section.sectionQuestions.forEach((question) => {
          if (newQuestion.questionId === question.questionId) {
            question.answerName = newQuestion.answerName;
          }
        });
      });
    },
    saveCallHandler: async function (question) {
      // Don't save when is view mode
      if (this.isViewMode) {
        return;
      }

      // Don't auto-save multimedia questions
      if (question.questionType === 4) {
        return;
      }

      // Don't auto-save chrono question
      // Fixme: We need to implement a local cache of answer calls to prevent flooding the backend
      // with request. There's an error with this type of question because it calls many times the
      // backend in a short time span, and it stores the wrong answer.
      if (question.questionType === 11) {
        return;
      }

      try {
        this.saveMode = true;
        this.origin = "autosave";

        await this.saveQuestions(question);
      } catch {
        const toastElement = this.$refs.statusErrorToast;
        await toastElement.error({
          message: this.$t("surveyQuestionScreen.saveCall.errorMessage"),
          header: this.$t("surveyQuestionScreen.saveCall.errorTitle"),
          duration: 5000,
        });
      }
    },
    updateProgressStatus: function () {
      this.progress = 0;
      this.questions.forEach((question) => {
        /* The possible answer status are:
            0 = pending
            1 = in progress
            2 = completed
         */
        if (question.answerStatus === 2) {
          this.progress = this.progress + 1;
        }
      });
    },
    createAnswerQuestion(question) {
      const q = {
        questionId: question.questionId,
        questionType: question.questionType,
        answerId: question.answerId,
        answerStatus: question.answerStatus,
        answerName: question.answerName,
        answerOptions: [],
        image: [],
      };
      if (
        question.questionType === 0 ||
        question.questionType === 1 ||
        question.questionType === 18
      ) {
        q.answerName = question.answerName;
      } else if ([2, 3, 6, 10].includes(question.questionType)) {
        // Those values are question with selectable options
        question.questionOptions.forEach((option) => {
          if (option.selected) {
            q.answerOptions.push({ questionOptionId: option.id });
          }
        });
      } else if ([12, 13, 15, 16, 17].includes(question.questionType)) {
        question.questionOptions.forEach((option) => {
          if (option.text) {
            q.answerOptions.push({
              questionOptionId: option.id,
              questionOptionText: option.text,
              hasVariableType: question.questionType === 13,
            });
          }
        });
      }
      return q;
    },
    createAnswerImageQuestion(imageQuestion) {
      const filteredImages = imageQuestion.image.filter((image) => {
        if (
          !(
            (image.id === null || image.id === undefined) &&
            image.deleted === true
          )
        ) {
          return image;
        }
      });
      const formatedQuestion = {
        questionId: imageQuestion.questionId,
        questionType: imageQuestion.questionType,
        answerId: imageQuestion.answerId,
        answerStatus: imageQuestion.answerStatus,
        answerName: "",
        answerOptions: [],
        image: filteredImages,
      };
      /* The images have a specific status, if it has elements to logically delete, it'll always be status 1, but we
        need to handle it as status 2 */
      if (imageQuestion.answerStatus === 1 && imageQuestion.image.length > 0) {
        formatedQuestion.answerStatus = 2;
      }
      return formatedQuestion;
    },
    async saveQuestions(singleQuestion = null) {
      const { assignmentId, roomId } = this;
      let answeredQuestionArray = [];
      if (singleQuestion) {
        if (
          singleQuestion.questionType === 4 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerImageQuestion(singleQuestion),
          ];
        } else if (singleQuestion.answerStatus === 2) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (
          singleQuestion.questionType === 12 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (
          singleQuestion.questionType === 13 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (singleQuestion.questionType === 10) {
          // For question type 10 (Selection Option Search), we save it even with answerStatus 0
          // to handle the case when the user clicks the "clear" button in the input.
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (
          singleQuestion.questionType === 15 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (
          singleQuestion.questionType === 16 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        } else if (
          singleQuestion.questionType === 17 &&
          singleQuestion.answerStatus !== 0
        ) {
          answeredQuestionArray = [
            ...answeredQuestionArray,
            this.createAnswerQuestion(singleQuestion),
          ];
        }
      } else {
        this.questions.forEach((question) => {
          if (question.questionType === 4 && question.answerStatus !== 0) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerImageQuestion(question),
            ];
          } else if (question.answerStatus === 2) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerQuestion(question),
            ];
          } else if (
            question.questionType === 12 &&
            question.answerStatus !== 0
          ) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerQuestion(question),
            ];
          } else if (
            question.questionType === 15 &&
            question.answerStatus !== 0
          ) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerQuestion(question),
            ];
          } else if (
            question.questionType === 16 &&
            question.answerStatus !== 0
          ) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerQuestion(question),
            ];
          } else if (
            question.questionType === 17 &&
            question.answerStatus !== 0
          ) {
            answeredQuestionArray = [
              ...answeredQuestionArray,
              this.createAnswerQuestion(question),
            ];
          }
        });
      }

      let location;
      if (getEnv("VUE_APP_LOCATION_DISABLED")?.toLowerCase() !== "true") {
        try {
          await this.requestMyLocation();
          location = store.state.myLocation;
        } catch (err) {
          console.log(err);
        }
      }

      const savingCallId = generateString(5);
      try {
        answeredQuestionArray = answeredQuestionArray.map((answer) => {
          if (answer.questionType === 4) {
            answer.image = answer.image.filter((answerImage) => {
              if (
                answerImage?.tempId &&
                this.alreadySavedImages.findIndex(
                  (id) => id === answerImage.tempId
                ) === -1
              ) {
                return true;
              } else {
                return !!(answerImage?.id && answerImage?.deleted);
              }
            });
          }

          return answer;
        });

        if (answeredQuestionArray.length > 0) {
          // We keep a list of call ids to know which one are still pending
          this.saveCallStack.push(savingCallId);

          const data = await this.saveRoomAnswers({
            assignmentId,
            roomId,
            answeredQuestionArray,
            currentLocation: location,
            justSave: this.saveMode,
            origin: this.origin,
          });

          for (const answer of answeredQuestionArray) {
            if (answer.questionType === 4) {
              for (const imageTempId of answer.image
                .map((img) => img.tempId)
                .filter((img) => img !== undefined)) {
                if (
                  this.alreadySavedImages.findIndex(
                    (id) => id === imageTempId
                  ) === -1
                ) {
                  this.alreadySavedImages.push(imageTempId);
                }
              }
            }
          }

          if (!data.success) {
            if (data?.cause === "reassignment") {
              this.$router.replace({
                name: "Welcome",
                params: {
                  message: this.$t("reassignment.message.error_message"),
                },
              });
              return;
            }
            throw {
              type: "unsuccess-response",
              payload: data.message,
            };
          }
        }
      } catch (err) {
        console.log("we have the error here!", err);
        throw {
          type: "bad-response",
          payload: {
            message:
              err.response != null
                ? err.response.data?.message
                : this.$t("default.error.message"),
            header: this.$t("error.default.connection.title"),
            duration: 5000,
          },
        };
      } finally {
        this.saveCallStack = this.saveCallStack.filter(
          (id) => id !== savingCallId
        );
      }
    },
    async saveAll(navigate = true) {
      if (!this.isViewMode) {
        try {
          store.commit("showLoading");
          await this.saveQuestions();

          if (navigate) {
            this.saveRoomAnswerRouterHandler();
          }

          return true;
        } catch (error) {
          switch (error.type) {
            case "unsuccess-response":
              this.showError(error.payload);
              break;
            case "bad-response": {
              const toastElement = this.$refs.statusErrorToast;
              await toastElement.error(error.payload);
              break;
            }
          }

          return false;
        } finally {
          store.commit("hideLoading");
        }
      }
    },
    saveRoomAnswerRouterHandler() {
      const places = JSON.parse(localStorage.getItem("places"));
      for (const element of places) {
        if (element.roomId === this.roomId) {
          element.answerStatus = 2;
          localStorage.setItem("places", JSON.stringify(places));
          break;
        }
      }
      if (
        this.userHasRole() === true &&
        !this.mandatoryCompleted &&
        !this.saveMode
      ) {
        this.$router.replace({
          name: "SurveyFinishedSuccessfully",
          params: {
            roomId: this.roomId,
            assignmentId: this.assignmentId,
            roomAssignmentId: this.roomAssignmentId,
            hasPermission: this.isViewMode,
          },
        });
      } else {
        this.$router.back();
      }
    },
    userHasRole() {
      let hasRole = false;
      const { roles } = store.state.loggedUser;

      const onlyRoles = roles.map((rol) => rol.role);

      if (
        onlyRoles.includes("SURVEYOR") ||
        onlyRoles.includes("SUPERSURVEYOR") ||
        onlyRoles.includes("SurveyorNoAddRoom")
      ) {
        hasRole = true;
      }

      return hasRole;
    },
    showError(msg) {
      this.$refs.userErrorBox.isVisible = true;
      if (msg.code) {
        this.errorMessage = this.$t(msg.code, msg.params ? msg.params : {});
      } else {
        this.errorMessage = this.$t(msg);
      }
    },
    countTotalQuestionsForProgressBar() {
      this.totalQuestions = this.questions.filter(
        (q) => (!q.isConditional || q.isVisible) && q.questionType !== 14
      ).length;
    },
    thenAction(result) {
      if (result.success) {
        const responseSections = result.surveySections;
        this.siteId = result.siteId;
        this.paginateBySections = result.paginateBySections;

        JSON.parse(localStorage.getItem("places")).forEach((r) => {
          if (r.roomAssignmentId === this.roomAssignmentId) {
            this.placeName = r.name;
            this.isRecurrent = r.recurrent;
          }
        });

        const questionsToShow = [];
        for (let question of result.questions) {
          if (question.questionOptions?.length > 0) {
            for (let questionOption of question.questionOptions) {
              if (questionOption.conditional) {
                if (questionOption.selected) {
                  questionOption.conditional.split(";").forEach((id) => {
                    questionsToShow.push(parseInt(id));
                  });
                }
              }
            }
          }
        }

        this.questions = result.questions.map((question) => {
          return {
            ...question,
            isVisible: !question.isConditional
              ? true
              : questionsToShow.indexOf(question.questionId) > -1,
          };
        });

        if (responseSections?.length > 0) {
          for (let responseSection of responseSections) {
            this.sections.push({
              ...responseSection,
              sectionQuestions: this.questions.filter(
                (question) =>
                  responseSection.sectionQuestions.findIndex(
                    (item) => item.id === question.questionId
                  ) !== -1
              ),
            });
          }
        }

        this.countTotalQuestionsForProgressBar();
        this.checkMandatorys();
        this.updateProgressStatus();
      } else {
        if (result?.cause === "reassignment") {
          this.$router.replace({
            name: "Welcome",
            params: { message: this.$t("reassignment.message.error_message") },
          });
          return;
        }

        this.showError(result.message);
      }
    },
    catchAction(error) {
      const toastElement = this.$refs.statusErrorToast;
      toastElement.error({
        message:
          error.response != null
            ? error.response.data?.message
            : this.$t("error.default.connection.message"),
        header: this.$t("error.default.connection.title"),
        duration: 5000,
      });
    },
    updateTransitionsHandler: async function ({ transitionId, comment }) {
      this.origin = "on update transition";
      const isSaved = await this.saveAll(false);

      if (isSaved) {
        try {
          const response = await this.transitionPhase({
            siteId: this.siteId,
            roomId: this.roomId,
            surveyId: this.survey?.id,
            assignmentId: this.assignmentId,
            transitionId: transitionId,
            comment: comment,
          });

          if (response?.success) {
            this.transitions = response.transitions;
            this.isActionEnabled = response.userHasRole;
            this.transitionComment = response.comment;

            this.saveRoomAnswerRouterHandler();
          } else {
            if (response?.message) {
              this.showError(response.message);
            }
          }
        } catch (error) {
          this.showError(error);
        }
      }
    },
    async onSaveQuestions(navigate) {
      if (!this.isViewMode) {
        this.saveMode = true;
        this.origin = "just save action";

        await this.saveAll(navigate);
      } else {
        this.$router.back();
      }
    },
    async onFinishQuestions() {
      this.saveMode = false;

      if (this.isRoomCompleted && this.hasWorkflow) {
        if (this.isRequestForApprovalPhase()) {
          this.transitionToRequestForApprovalPhase();
        } else {
          this.showTransitionModal();
        }
      } else {
        this.origin = "on finish question";
        await this.saveAll();
      }
    },
    transitionToRequestForApprovalPhase() {
      this.selectedTransition = this.transitions.filter(
        (transition) => transition.name === "Request for approval"
      )[0];
      this.showCommentModal = true;
    },
    showTransitionModal() {
      this.isTransitionModalVisible = true;
    },
    modalDismissHandler: function () {
      this.selectedTransition = null;
      this.showCommentModal = false;
    },
    modalConfirmHandler: function (comment) {
      if (this.selectedTransition != null) {
        this.updateTransitionsHandler({
          transitionId: this.selectedTransition.id,
          comment,
        });

        this.selectedTransition = null;
        this.showCommentModal = false;
      }
    },
    async onResetForm() {
      for (let question of this.questions) {
        question.answerStatus = 0;
        question.answerName = null;
        question.image = [];

        const newQuestionOptions = question.questionOptions.map(
          (questionOption) => {
            return {
              ...questionOption,
              selected: false,
              selectedAnswerOptionId: 0,
            };
          }
        );

        Vue.set(question, "questionOptions", newQuestionOptions);
      }

      this.updateProgressStatus();

      try {
        store.commit("showLoading");
        const response = await this.resetRoomSurvey({
          roomAssignmentId: this.roomAssignmentId,
        });
        if (!response.success) {
          this.showError(response.message);
        }
      } catch (error) {
        this.catchAction(error);
      } finally {
        store.commit("hideLoading");
      }

      this.isConfirmResetModalVisible = false;
    },
  },
  async created() {
    try {
      store.commit("showLoading");

      this.isViewMode = store.state.isSurveyViewMode;

      if (this.$store.getters.UserHasPermission("Always Can Answer Question")) {
        this.isViewMode = false;
      }

      this.roomId = Number.parseInt(this.$route.params.roomId);
      this.assignmentId = Number.parseInt(this.$route.params.assignmentId);
      this.roomAssignmentId = Number.parseInt(
        this.$route.params.roomAssignmentId
      );

      this.thenAction(
        await this.getQuestions({
          roomId: this.roomId,
          assignmentId: this.assignmentId,
          roomAssignmentId: this.roomAssignmentId,
        })
      );

      const responseT = await this.getTransitionOptions({
        surveyId: null,
        assignmentId: this.assignmentId,
      });
      if (responseT.success) {
        this.transitions = responseT.transitions;
        this.transitionComment = responseT.comment;
        this.phaseName = responseT.phaseName;

        this.finishButtonText = this.getFinishButtonText();
      } else {
        this.showError(responseT.message);
      }
    } catch (error) {
      this.catchAction(error);
    } finally {
      store.commit("hideLoading");
    }
  },
};
</script>

<style scoped>
.survey-bg {
  background-color: #e9ecef;
}
</style>
